import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useApiClient } from '../../config/axios';

interface AutocompleteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  contextKey?: string;
  entityTypes?: string[];
  onBlur?: () => void;
  onSelect?: (value: string, metadata?: DatabaseSuggestion) => void;
  staticOptions?: string[];
}

interface DatabaseSuggestion {
  id: string;
  name?: string;
  display_name: string;
  entity_type: string;
  source: string;
  usage_count: number;
  autofill_data?: any;
}

export function AutocompleteDropdown({
  value,
  onChange,
  placeholder = "Search and select...",
  contextKey = '',
  entityTypes = [],
  onBlur,
  onSelect,
  staticOptions = []
}: AutocompleteDropdownProps) {
  const apiClient = useApiClient();
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [dbSuggestions, setDbSuggestions] = useState<DatabaseSuggestion[]>([]);

  // Initialize with static options if provided
  useEffect(() => {
    if (staticOptions.length > 0) {
      setOptions(staticOptions);
    }
  }, [staticOptions]);

  // Load suggestions from database when user types
  const loadSuggestions = async (query: string = '') => {
    console.log(`FRONTEND DEBUG: contextKey=${contextKey}, query="${query}", staticOptions=${staticOptions.length}`);
    // If we have static options, use them instead of database
    if (staticOptions.length > 0) {
      if (!query || query.length < 1) {
        setOptions(staticOptions);
        return;
      }

      // Filter static options based on query
      const filteredStatic = staticOptions.filter(option =>
        option.toLowerCase().includes(query.toLowerCase())
      );
      setOptions(filteredStatic);
      return;
    }

    // Original database logic for when no static options
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    try {
      setLoading(true);
      console.log('AXIOS DEBUG: Making API call to: /dropdown/suggestions');
      const response = await apiClient.get('/dropdown/suggestions', {
        params: {
          query: query || '',
          context: contextKey,
          entity_types: entityTypes.length > 0 ? entityTypes.join(',') : undefined,
          limit: 50
        }
      });

      const suggestions = response.data?.suggestions || [];
      // Store the raw suggestions for metadata lookup
      setDbSuggestions(suggestions);

      // Show different values based on context
      const optionStrings = suggestions.map((s: DatabaseSuggestion) => {
        if (contextKey === 'data_index') {
          // For dataIndex column, show the actual data_index (id)
          return s.id;
        } else {
          // For text/label columns, show display_name with entity_type for context
          return `${s.display_name} (${s.entity_type})`;
        }
      });

      console.log(`FRONTEND DEBUG: Got ${suggestions.length} suggestions for context '${contextKey}':`, optionStrings);
      setOptions(optionStrings);
    } catch (error) {
      console.error('AXIOS ERROR - Failed to load suggestions:', error);
      console.error('AXIOS ERROR - Request was: /dropdown/suggestions?query=' + query + '&context=' + contextKey + '&entity_types=' + entityTypes.join(',') + '&limit=50');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Autocomplete
      freeSolo
      openOnFocus
      disableClearable
      selectOnFocus={false}
      size="small"
      loading={loading}
      options={options}
      value={value}
      filterOptions={(options) => options}
      onChange={(event, newValue) => {
        // This fires when user SELECTS from dropdown
        if (newValue !== null) {
          const selectedValue = typeof newValue === 'string' ? newValue : String(newValue || '');

          // Find corresponding database suggestion
          let metadata: DatabaseSuggestion | undefined;
          if (contextKey === 'data_index') {
            // For dataIndex, selectedValue IS the data_index (s.id)
            metadata = dbSuggestions.find(s => s.id === selectedValue);
            onChange(selectedValue);
          } else {
            // For text columns, selectedValue is "Display Name (entity_type)"
            // Extract just the display_name for the field
            const displayName = selectedValue.includes(' (') ? selectedValue.split(' (')[0] : selectedValue;
            const entityType = selectedValue.match(/\(([^)]+)\)$/)?.[1];

            metadata = dbSuggestions.find(s =>
              s.display_name === displayName &&
              (!entityType || s.entity_type === entityType)
            );

            // Store only the display_name in the field (no entity_type)
            onChange(displayName);
          }

          if (onSelect) {
            onSelect(contextKey === 'data_index' ? selectedValue : selectedValue.split(' (')[0], metadata);
          }
        }
      }}
      onInputChange={(event, inputValue, reason) => {
        // This fires on every keystroke - handle typing
        if (reason === 'input') {
          onChange(inputValue);
          loadSuggestions(inputValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          onBlur={onBlur}
          variant="standard"
          size="small"
          onKeyDown={(e) => {
            // Allow space key to pass through normally
            if (e.key === ' ') {
              e.stopPropagation();
            }
          }}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            style: {
              fontSize: '12px',
              padding: '4px 8px',
              minHeight: '20px'
            }
          }}
        />
      )}
      style={{ width: '100%' }}
      componentsProps={{
        popper: {
          style: {
            fontSize: '12px'
          }
        }
      }}
    />
  );
}